/**
 * Isolated refreshToken API call for new httpOnly auth flow
 *
 * Based on services/api/accessToken/refreshToken.js with modifications:
 * - Uses newAuthUfeApi.makeRequest instead of native fetch
 * - NO Seph-Access-Token header (x-api-key used by newAuthUfeApi)
 * - Empty request body (tokens sent via httpOnly cookie)
 * - Returns profileStatus in response
 * - Stores profileSecurityStatus and isSSI to localStorage
 */
import newAuthUfeApi from 'services/auth/newFlow/api/newAuthUfeApi';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import userUtils from 'utils/User';
import cookieUtils from 'utils/Cookies';
import { PROFILE_STATUS_COOKIE_NAME } from 'services/auth/newFlow/constants';

const REFRESH_TOKEN_URL = '/gway/v1/dotcom/auth/v2/refreshToken';
const BACKOFF_DELAYS = [2000, 4000, 6000]; // Retry delays in ms

/**
 * Get the cookie domain for setting cookies
 * Extracts the root domain from current hostname (e.g., local.sephora.com → .sephora.com)
 */
function getCookieDomain() {
    if (typeof window === 'undefined') {
        return '';
    }

    const hostname = window.location.hostname;

    // For localhost/IP, don't set domain attribute (browser won't allow it)
    if (hostname === 'localhost' || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
        return '';
    }

    // Extract root domain: local.sephora.com → .sephora.com
    const parts = hostname.split('.');

    if (parts.length >= 2) {
        const domain = '.' + parts.slice(-2).join('.');

        return `domain=${domain}; `;
    }

    return '';
}

async function makeRequestWithTokens(payload, headers, url) {
    return newAuthUfeApi
        .makeRequest(
            url,
            {
                url,
                method: 'POST',
                body: JSON.stringify(payload),
                headers
            },
            {
                skipTokenCheck: true // CRITICAL: Prevent infinite loop (refreshToken shouldn't check tokens before calling itself)
            }
        )
        .then(data => {
            if (data.errorCode || data.errors) {
                return Promise.reject(data);
            }

            // Store profileStatus in localStorage (0, 2, 3, or 4)
            // Tokens are NOT stored - they're in httpOnly cookies
            data.profileSecurityStatus != null && Storage.local.setItem(LOCAL_STORAGE.PROFILE_SECURITY_STATUS, data.profileSecurityStatus);

            // Store to both isSSI (new) and staySignIn (old) for backward compatibility
            if (data.isSSI != null) {
                Storage.local.setItem(LOCAL_STORAGE.IS_SSI, data.isSSI);
                Storage.local.setItem(LOCAL_STORAGE.STAY_SIGN_IN, data.isSSI);

                //Fix for (ACS-3132): Manually set isSSI cookie as safety net
                // Backend sets 'sks' via Set-Cookie header, but we also set it manually to ensure it exists
                // We also set legacy 'isSSI' name for backward compatibility with old code
                const isProd = typeof Sephora !== 'undefined' && Sephora.UFE_ENV === 'PROD';
                const isSSITTL = data.isSSI
                    ? isProd
                        ? 150 * 24 * 60 * 60
                        : 10 * 60 // true: PROD=150 days, Lower envs=10 mins
                    : isProd
                        ? 60 * 60
                        : 5 * 60; // false: PROD=60 mins, Lower envs=5 mins
                const IS_SSI_COOKIE_NAME = 'sks'; // Backend standard name
                document.cookie = `${IS_SSI_COOKIE_NAME}=${data.isSSI}; ${getCookieDomain()}path=/; max-age=${isSSITTL}; Secure; SameSite=Lax`;
                document.cookie = `isSSI=${data.isSSI}; ${getCookieDomain()}path=/; max-age=${isSSITTL}; Secure; SameSite=Lax`; // Legacy name for backward compat
            }

            //Fix for (ACS-3132): Manually set readable profileStatus cookie as safety net
            // Backend sets 'sis' via Set-Cookie header, but we also set it manually to ensure it exists
            // We also set legacy 'profileStatus' name for backward compatibility with old code
            if (data.profileSecurityStatus != null) {
                const isProd = typeof Sephora !== 'undefined' && Sephora.UFE_ENV === 'PROD';
                const profileStatusTTL = isProd ? 60 * 60 : 5 * 60; // PROD: 60 mins, Lower envs: 5 mins
                const profileStatusValue = data.profileSecurityStatus + '|' + Math.floor(Date.now() / 1000 + profileStatusTTL);
                const PROFILE_STATUS_COOKIE_NAME_NEW = 'sis'; // Backend standard name
                document.cookie = `${PROFILE_STATUS_COOKIE_NAME_NEW}=${profileStatusValue}; ${getCookieDomain()}path=/; max-age=${profileStatusTTL}; Secure; SameSite=Lax`;
                document.cookie = `profileStatus=${profileStatusValue}; ${getCookieDomain()}path=/; max-age=${profileStatusTTL}; Secure; SameSite=Lax`; // Legacy name for backward compat
            }

            // Store tokens for backward compatibility (to be removed later)
            // once BE is fully on httpOnly cookie flow
            if (data.accessToken) {
                Storage.local.setItem(LOCAL_STORAGE.AUTH_ACCESS_TOKEN, data.accessToken);
            }

            if (data.refreshToken) {
                Storage.local.setItem(LOCAL_STORAGE.AUTH_REFRESH_TOKEN, data.refreshToken);
            }

            return data;
        });
}

async function buildRequestConfig(options = {}) {
    const url = REFRESH_TOKEN_URL;

    let headers = {
        'Content-Type': 'application/json',
        'x-requested-source': 'web'
    };

    // Merge any additional headers from options
    if (options.headers) {
        headers = {
            ...headers,
            ...options.headers
        };
    }

    // For httpOnly flow: empty payload (tokens come from cookie)
    // Only include email if provided (for SSI flows)
    const email = options.email || userUtils.getProfileEmail();
    const payload = email ? { email } : {};

    // MIGRATION FIX (ACS-3132): Handle switch from old auth flow to new auth flow
    // If httpOnly cookies don't exist but localStorage has old tokens, send them in request body
    // This allows backend to migrate user from localStorage tokens → httpOnly cookies
    // Check both 'sis' (backend standard) and 'profileStatus' (legacy) for backward compatibility
    let profileStatusCookie = cookieUtils.read(PROFILE_STATUS_COOKIE_NAME);

    if (!profileStatusCookie) {
        profileStatusCookie = cookieUtils.read('profileStatus'); // Fallback to legacy name for backward compat
    }

    const validValues = ['0', '2', '3', '4'];
    const parts = profileStatusCookie ? profileStatusCookie.split('|') : [];
    const hasHttpOnlyCookie = parts[0] && validValues.includes(parts[0]);

    let didSendLocalStorageTokens = false; // Track if we're doing migration

    if (!hasHttpOnlyCookie) {
        const oldRefreshToken = Storage.local.getItem(LOCAL_STORAGE.AUTH_REFRESH_TOKEN);
        const oldAccessToken = Storage.local.getItem(LOCAL_STORAGE.AUTH_ACCESS_TOKEN);

        if (oldRefreshToken) {
            Sephora.logger.info('[New Auth Flow] MIGRATION: Sending localStorage refresh token in request body (no httpOnly cookie found)');
            payload.refreshToken = oldRefreshToken;
            didSendLocalStorageTokens = true; // Mark as migration attempt

            // Also send access token for backward compatibility
            if (oldAccessToken) {
                payload.accessToken = oldAccessToken;
            }
        }
    }

    return { url, headers, payload, didSendLocalStorageTokens };
}

async function refreshToken(bodyJSON = {}, options = {}, retryCount = 0) {
    const { url, headers, payload, didSendLocalStorageTokens } = await buildRequestConfig({ ...bodyJSON, ...options });

    try {
        const result = await makeRequestWithTokens(payload, headers, url);

        // MIGRATION FIX (ACS-3132): Do NOT clear tokens - they're needed for backward compat
        // Migration = we sent localStorage tokens AND got back logged-in status
        const wasMigration = didSendLocalStorageTokens && result.profileSecurityStatus > 0;

        if (wasMigration) {
            // The backend returns NEW valid tokens in the response
            // These are stored in localStorage (lines 52-58) and used by other parts of the app
            // DO NOT clear them - clearing triggers logout logic when code expects tokens to exist
            Sephora.logger.info('[New Auth Flow] MIGRATION COMPLETE: Keeping new tokens in localStorage for backward compatibility');
        }

        return result;
    } catch (error) {
        // Retry logic with exponential backoff
        if (retryCount < BACKOFF_DELAYS.length) {
            const delay = BACKOFF_DELAYS[retryCount];

            Sephora.logger.info(`[refreshToken] Retrying (attempt ${retryCount + 1}/${BACKOFF_DELAYS.length}) after ${delay}ms`, {
                error: error.message || error
            });

            await new Promise(resolve => setTimeout(resolve, delay));

            return refreshToken(bodyJSON, options, retryCount + 1);
        }

        // No more retries - throw error to trigger login modal
        Sephora.logger.error('[refreshToken] All retry attempts exhausted', {
            retryCount,
            error: error.message || error
        });

        throw error;
    }
}

export default {
    refreshToken
};
